/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, X } from "lucide-react";
import ColumnChooser from "@/components/ColumnChooser";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";
import { useAppSettings } from "@/hooks/useAppSettings";

const STATUSES = ["Orders In", "Picking", "To Invoice", "Completed"];
const STATUS_COLOR = {
  "Orders In": "brand", "Picking": "warning", "To Invoice": "warning",
  "Completed": "success",
};

export default function PortalOrdersPage() {
  const settings = useAppSettings();
  // Portal-specific labels take precedence; fall back to Pipeline aliases; finally canonical.
  const labelFor = (s) =>
    settings?.Portal_Order_Stage_Labels?.[s]
    || settings?.Order_Stage_Labels?.[s]
    || s;
  const [searchParams] = useSearchParams();
  const asCompany = searchParams.get("as_company");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const orderParams = asCompany ? { company_id: asCompany } : {};
      const [o, p] = await Promise.all([
        api.get("/orders", { params: orderParams }),
        api.get("/inventory"),
      ]);
      setOrders(o.data); setProducts(p.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));

  const allColumns = [
    { key: "Order_ID", label: "Order ID", cell: (o) => <span className="font-mono text-xs">{o.id.slice(0, 8)}</span> },
    { key: "Product", label: "Product", cell: (o) => <span className="font-semibold">{productMap[o.Product_ID]?.Product_Name || "—"}</span> },
    { key: "Quantity_Ordered", label: "Qty", align: "right", cell: (o) => <span className="font-mono font-bold">{o.Quantity_Ordered}</span> },
    { key: "Order_Status", label: "Status", cell: (o) => <span className={`es-badge es-badge-${STATUS_COLOR[o.Order_Status] || "neutral"}`}>{labelFor(o.Order_Status)}</span> },
    { key: "Delivery_Address", label: "Delivery", cell: (o) => <span className="text-sm">{o.Delivery_Address || "—"}</span> },
    { key: "Created", label: "Created", cell: (o) => <span className="font-mono text-xs">{o.created_date?.slice(0, 10)}</span> },
    { key: "Notes", label: "Notes", defaultVisible: false, cell: (o) => <span className="text-xs text-zinc-500">{o.Notes || "—"}</span> },
  ];

  const { visibleColumns, orderedColumns, isVisible, toggleColumn, moveColumn, reset } =
    useColumnPrefs("portal_orders", allColumns);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="es-overline text-brand-600 mb-2">// your orders</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">My Orders</h1>
          <p className="text-sm text-zinc-600 mt-1">{orders.length} order(s) in your account</p>
        </div>
        <div className="flex items-center gap-2">
          <ColumnChooser
            tableLabel="portal_orders"
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            moveColumn={moveColumn}
            reset={reset}
          />
          <button data-testid="portal-new-order-button" onClick={() => setShowNew(true)} disabled={Boolean(asCompany)} title={asCompany ? "Disabled in admin view" : ""} className="es-btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
            <Plus className="w-4 h-4" /> Request order
          </button>
        </div>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      <div className="es-card overflow-x-auto">
        <table className="es-table">
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <th key={c.key} className={[c.align === "right" ? "text-right" : "", c.width || ""].join(" ")}>
                  {c.header ? c.header() : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">Loading…</td></tr>}
            {!loading && orders.length === 0 && <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">No orders yet.</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} data-testid={`portal-order-${o.id}`}>
                {visibleColumns.map((c) => (
                  <td key={c.key} className={c.align === "right" ? "text-right" : ""}>{c.cell(o)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewOrderModal products={products} onClose={() => setShowNew(false)} onCreated={(o) => { setOrders((prev) => [o, ...prev]); setShowNew(false); }} />}
    </div>
  );
}

function NewOrderModal({ products, onClose, onCreated }) {
  const [form, setForm] = useState({ Product_ID: "", Quantity_Ordered: 0, Delivery_Address: "", Notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      // Resellers don't pass Company_ID; backend infers from auth context. Need to pass something — fetch from /portal/overview
      const overview = await api.get("/portal/overview");
      const payload = {
        Company_ID: overview.data.company.id,
        Product_ID: form.Product_ID,
        Quantity_Ordered: Number(form.Quantity_Ordered),
        Order_Status: "Inquiry",
        Delivery_Address: form.Delivery_Address,
        Notes: form.Notes,
      };
      const { data } = await api.post("/orders", payload);
      onCreated(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-zinc-900/60 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm border border-zinc-300 w-full max-w-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <h3 className="font-display text-lg font-bold">Request Order</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="es-label">Product *</label>
            <select required value={form.Product_ID} onChange={(e) => u("Product_ID", e.target.value)} className="es-select w-full">
              <option value="">Select…</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.Product_Name}</option>)}
            </select>
          </div>
          <div>
            <label className="es-label">Quantity *</label>
            <input required type="number" min="1" value={form.Quantity_Ordered} onChange={(e) => u("Quantity_Ordered", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Delivery Address</label>
            <input value={form.Delivery_Address} onChange={(e) => u("Delivery_Address", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Notes</label>
            <textarea rows={2} value={form.Notes} onChange={(e) => u("Notes", e.target.value)} className="es-input h-auto py-2" />
          </div>
          {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-sm">{err}</div>}
          <div className="flex justify-end gap-3"><button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button><button disabled={saving} type="submit" className="es-btn-primary">{saving ? "Submitting…" : "Submit request"}</button></div>
        </form>
      </div>
    </div>
  );
}
